import React, { useContext as useContextCore } from 'react'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { StatusEnum } from '@ztechapps/common.contract'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import merge from 'lodash/merge'
import { UnitOfMeasureAddFormInput } from './UnitOfMeasureAddFormInput'
import { useUnitOfMeasureAdd } from './useUnitOfMeasureAdd'
import type { SelectOption } from '@ztechapps/webui.input'

export type UnitOfMeasureAddFormValues = {
  code: string
  name: string
  symbol: string
  category: SelectOption
  conversionFactor: number
  status: SelectOption
}

export type UnitOfMeasureAddComponent = {
  formInput: () => React.ReactElement | null
  saveCancelButtons: (props: SaveCancelButtonsProps) => React.ReactElement | null
}

const defaultComponents: UnitOfMeasureAddComponent = {
  formInput: UnitOfMeasureAddFormInput,
  saveCancelButtons: FormSaveCancelButtons,
}

export type UnitOfMeasureAddProviderProps = {
  onSubmit?: (values: UnitOfMeasureAddFormValues) => Promise<void>
  onReset?: () => void
  children: React.ReactNode
  initialValues?: UnitOfMeasureAddFormValues
  components?: UnitOfMeasureAddComponent
}

export type UnitOfMeasureAddContext = {
  validate: (value: UnitOfMeasureAddFormValues) => Partial<Record<keyof UnitOfMeasureAddFormValues, string>>
} & Omit<UnitOfMeasureAddProviderProps, 'children'>

const context = React.createContext<UnitOfMeasureAddContext>({} as UnitOfMeasureAddContext)
const CoreProvider = context.Provider

export const UnitOfMeasureAddProvider = ({
  children,
  components,
  onSubmit,
  initialValues,
  ...props
}: UnitOfMeasureAddProviderProps) => {
  initialValues = initialValues ?? {
    code: '',
    name: '',
    symbol: '',
    category: {
      value: 'COUNT',
      label: 'COUNT',
    },
    conversionFactor: 1,
    status: {
      value: StatusEnum.Active,
      label: StatusEnum.Active,
    },
  }

  const { onSubmit: defaultOnSubmit } = useUnitOfMeasureAdd()

  onSubmit = onSubmit ?? defaultOnSubmit

  //merge the components with the default components
  const mergedComponents = merge(defaultComponents, components)

  const validate = (value: UnitOfMeasureAddFormValues) => {
    const errors: Partial<Record<keyof UnitOfMeasureAddFormValues, string>> = {}
    if (!value?.code) {
      errors.code = 'Required'
    }
    if (!value?.name) {
      errors.name = 'Required'
    }
    if (!value?.symbol) {
      errors.symbol = 'Required'
    }
    if (!value?.category) {
      errors.category = 'Required'
    }
    if (!value?.conversionFactor || value.conversionFactor <= 0) {
      errors.conversionFactor = 'Must be greater than 0'
    }
    if (!value?.status) {
      errors.status = 'Required'
    }

    return errors
  }

  return (
    <CoreProvider value={{ initialValues, validate, components: mergedComponents, onSubmit, ...props }}>
      {children}
    </CoreProvider>
  )
}

export const useUnitOfMeasureAddContext = () => useContextCore(context)
